<p-confirmdialog />
<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <!-- Header -->
  <header>
    <div class="headline">
      <h1>
        @if (!isNewRecord()) {
          {{ 'InventorySession_Title' | translateWF: 'Inventory Session' }}: {{ session()?.name || session()?.number }}
        } @else {
          {{ 'InventorySession_New' | translateWF: 'New Inventory Session' }}
        }
      </h1>
      @if (!isNewRecord() && session()) {
        <p-tag [value]="statusLabel()" [severity]="statusSeverity()" />
      }
    </div>
    <div class="buttons">
      @if (!isNewRecord() && session()) {
        <p-splitButton
          [label]="'InventorySession_Export' | translateWF: 'Export'"
          icon="pi pi-print"
          severity="secondary"
          variant="outlined"
          (onClick)="exportSession(false)"
          [model]="exportMenuItems"
        />
      }
      @if (!isNewRecord() && isOpen()) {
        <p-button [label]="'InventorySession_Close' | translateWF: 'Close Session'" icon="pi pi-lock" severity="warn" (onClick)="closeSession()" />
      }
      <p-button [label]="'Common_Back' | translateWF: 'Back'" icon="pi pi-arrow-left" variant="outlined" (onClick)="navigateBack()" />
    </div>
  </header>

  <!-- Main Content -->
  <div class="content">
    <div class="col">
      <!-- Create Mode -->
      @if (isNewRecord()) {
        <p-panel [header]="'InventorySession_Setup' | translateWF: 'Session Setup'">
          <p-card data-variant="flat">
            <div class="grid-12">
              <div class="span-12-6-4">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_Name' | translateWF: 'Name' }}</label>
                  <input pInputText [formControl]="form.controls.name" class="w-full" [placeholder]="'InventorySession_EnterName' | translateWF: 'Enter session name (optional)'" />
                </div>
              </div>
              <div class="span-12-6-4">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_Location' | translateWF: 'Location' }} *</label>
                  <p-select
                    fluid
                    [options]="warehouseOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [formControl]="form.controls.locationCode"
                    [placeholder]="'InventorySession_SelectLocation' | translateWF: 'Select a warehouse'"
                    [filter]="true"
                    filterBy="label"
                  />
                </div>
              </div>
            </div>

            <div class="mt-4">
              <p-button [label]="'InventorySession_Create' | translateWF: 'Create Session'" icon="pi pi-plus" (onClick)="createSession()" [disabled]="!form.valid" />
            </div>
          </p-card>
        </p-panel>
      }

      <!-- View / Scan Mode -->
      @if (!isNewRecord() && session()) {
        <!-- Session Info -->
        <p-panel [header]="'InventorySession_Details' | translateWF: 'Session Details'">
          <p-card data-variant="flat">
            <div class="grid-12">
              <div class="span-6-3">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_Location' | translateWF: 'Location' }}</label>
                  <span class="font-semibold">{{ locationDisplay() }}</span>
                </div>
              </div>
              <div class="span-6-3">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_TotalScanned' | translateWF: 'Total Scanned' }}</label>
                  <span class="font-semibold text-lg">{{ session()!.totalChildScannedCount }}</span>
                </div>
              </div>
              <div class="span-6-3">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_CreatedBy' | translateWF: 'Created By' }}</label>
                  <span class="font-semibold">{{ session()!.createdByUserName }}</span>
                </div>
              </div>
              <div class="span-6-3">
                <div class="form-box">
                  <label class="mb-2">{{ 'InventorySession_Number' | translateWF: 'Number' }}</label>
                  <span class="font-semibold">{{ session()!.number }}</span>
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Child Sessions Pills -->
        <p-panel [header]="'InventorySession_ChildSessions' | translateWF: 'Child Sessions'">
          <p-card data-variant="flat">
            @if (closedChildrenCount() > 0) {
              <div class="row-sm-start-center mb-3">
                <p-toggleSwitch [ngModel]="showClosedChildren()" (ngModelChange)="showClosedChildren.set($event)" />
                <span class="text-sm"> {{ 'InventorySession_ShowClosed' | translateWF: 'Show Closed' }} ({{ closedChildrenCount() }}) </span>
              </div>
            }
            <div class="flex flex-wrap gap-2 items-center">
              @for (child of filteredChildren(); track child.id) {
                <button
                  class="child-pill"
                  [class.child-pill--selected]="selectedChildId() === child.id"
                  [class.child-pill--closed]="child.status === 1"
                  (click)="selectChild(child.id)"
                  (contextmenu)="onChildContextMenu($event, child)"
                >
                  <span class="child-pill__location">{{ child.locationCode }}</span>
                  <span class="child-pill__count">{{ child.scannedCount }}</span>
                  <i
                    class="child-pill__action pi"
                    [class.pi-lock]="child.status === 0"
                    [class.pi-lock-open]="child.status === 1"
                    [pTooltip]="child.status === 0 ? 'Close' : 'Reopen'"
                    tooltipPosition="top"
                    (click)="toggleChildStatus(child, $event)"
                  ></i>
                </button>
              }
              @if (isOpen()) {
                <button class="child-pill child-pill--add" (click)="openCreateChildDialog()">
                  <i class="pi pi-plus"></i>
                </button>
              }
              @if (!session()!.children?.length) {
                <span class="text-gray-400">{{
                  'InventorySession_NoChildren'
                    | translateWF
                      : 'No child sessions yet. Click + to
              create one.'
                }}</span>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Child Session Context Menu -->
        <p-contextMenu #childContextMenu [model]="childContextMenuItems" appendTo="body" />

        <!-- Selected Child: Scanner + Scans -->
        @if (selectedChild(); as child) {
          <!-- Scanner Section (only when child is Open) -->
          @if (selectedChildIsOpen()) {
            <p-panel [header]="'InventorySession_ScanItems' | translateWF: 'Scan Lot Items'">
              <p-card data-variant="flat">
                <!-- Scan Mode Toggle -->
                <div class="row-between-center mb-3">
                  <span></span>
                  <div class="row-start-center gap-3">
                    <span class="text-sm font-semibold" [class.text-primary]="scanMode() === 'nexus'">NEXUS QR</span>
                    <p-toggleSwitch [ngModel]="scanMode() === 'edr'" (ngModelChange)="scanMode.set($event ? 'edr' : 'nexus')" />
                    <span class="text-sm font-semibold" [class.text-primary]="scanMode() === 'edr'">EDR Barcode</span>
                  </div>
                </div>
                @if (scanMode() === 'edr') {
                  <p-message severity="info" class="mb-3" text="EDR mode: Scan EDR barcode to look up Nexus roll number, then scan into session." />
                }
                <div class="grid-12 mb-4">
                  <div class="span-12-6">
                    <div class="form-box">
                      <label class="mb-2">{{ 'InventorySession_SearchLotItem' | translateWF: 'Search by Number' }}</label>
                      <div class="p-inputgroup">
                        <span class="p-inputgroup-addon"><i class="pi pi-search"></i></span>
                        <input type="text" pInputText [(ngModel)]="lotItemSearchQuery" (keydown.enter)="onManualSearch()" />
                        <p-button icon="pi pi-arrow-right" (onClick)="onManualSearch()" [disabled]="!lotItemSearchQuery.trim()" />
                      </div>
                    </div>
                  </div>
                </div>
                <app-qr-scanner-modal (isScannedCode)="onQrCodeScanned($event)" />
              </p-card>
            </p-panel>
          }

          <!-- Scans Table -->
          <p-panel [header]="('InventorySession_ScannedItems' | translateWF: 'Scanned Items') + ' — ' + child.locationCode">
            <p-card data-variant="compact">
              <p-tag [value]="child.location?.fullName | localized"></p-tag>
              <div class="row-end-stretch mb-2">
                <p-button icon="pi pi-refresh" [text]="true" [rounded]="true" (onClick)="loadScans()" [pTooltip]="'Common_Refresh' | translateWF: 'Refresh'" />
              </div>

              <p-table [value]="filteredScans()">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 60px">#</th>
                    <th>{{ 'InventorySession_LotItemNumber' | translateWF: 'Lot Item Number' }}</th>
                    <th>{{ 'InventorySession_LocationAtScan' | translateWF: 'Location at Scan' }}</th>
                    <th>{{ 'InventorySession_InsideLocation' | translateWF: 'Inside Location' }}</th>
                    <th>{{ 'InventorySession_ScannedBy' | translateWF: 'Scanned By' }}</th>
                    <th>{{ 'InventorySession_ScannedAt' | translateWF: 'Scanned At' }}</th>
                    @if (canDeleteScans()) {
                      <th style="width: 60px"></th>
                    }
                  </tr>
                  <tr class="filter-row cs-datatable-filter-row">
                    <th></th>
                    <th>
                      <input
                        pInputText
                        size="small"
                        class="w-full"
                        [ngModel]="filterLotItem()"
                        (ngModelChange)="filterLotItem.set($event)"
                        [placeholder]="'Common_Filter' | translateWF: 'Filter...'"
                      />
                    </th>
                    <th>
                      <input
                        pInputText
                        size="small"
                        class="w-full"
                        [ngModel]="filterLocation()"
                        (ngModelChange)="filterLocation.set($event)"
                        [placeholder]="'Common_Filter' | translateWF: 'Filter...'"
                      />
                    </th>
                    <th>
                      <p-select
                        size="small"
                        class="w-full"
                        [options]="insideLocationFilterOptions"
                        [ngModel]="filterInsideLocation()"
                        (ngModelChange)="filterInsideLocation.set($event)"
                        optionLabel="label"
                        optionValue="value"
                        [showClear]="true"
                        [placeholder]="'Common_All' | translateWF: 'All'"
                      />
                    </th>
                    <th>
                      <input
                        pInputText
                        size="small"
                        class="w-full"
                        [ngModel]="filterScannedBy()"
                        (ngModelChange)="filterScannedBy.set($event)"
                        [placeholder]="'Common_Filter' | translateWF: 'Filter...'"
                      />
                    </th>
                    <th></th>
                    @if (canDeleteScans()) {
                      <th></th>
                    }
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-scan let-i="rowIndex">
                  <tr>
                    <td>{{ filteredScans().length - i }}</td>
                    <td class="font-semibold">{{ scan.lotItemNumber }}</td>
                    <td>{{ scan.locationCodeAtScan }}</td>
                    <td>
                      <p-tag [value]="scan.insideLocation ? 'Yes' : 'No'" [severity]="scan.insideLocation ? 'success' : 'warn'" />
                    </td>
                    <td>{{ scan.scannedByUserName }}</td>
                    <td>{{ scan.scannedAt | hnDate }}</td>
                    @if (canDeleteScans()) {
                      <td>
                        <p-button
                          icon="pi pi-trash"
                          severity="danger"
                          variant="text"
                          [rounded]="true"
                          [pTooltip]="'Common_Delete' | translateWF: 'Delete'"
                          (onClick)="deleteScan(scan)"
                        />
                      </td>
                    }
                  </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td [attr.colspan]="canDeleteScans() ? 7 : 6" class="text-center py-8">
                      {{ 'InventorySession_NoScans' | translateWF: 'No scans yet' }}
                    </td>
                  </tr>
                </ng-template>
              </p-table>

              <p-paginator
                [rows]="scansPageSize"
                [totalRecords]="scansTotalRecords"
                [first]="(scansPage - 1) * scansPageSize"
                (onPageChange)="onScansPageChange($event)"
                [rowsPerPageOptions]="[10, 25, 50, 100]"
              ></p-paginator>
            </p-card>
          </p-panel>
        }

        <!-- No child selected message -->
        @if (!selectedChild() && session()!.children?.length) {
          <p-panel>
            <p-card data-variant="flat">
              <div class="text-center py-8 text-gray-400">
                {{ 'InventorySession_SelectChild' | translateWF: 'Select a child session above to view its scans' }}
              </div>
            </p-card>
          </p-panel>
        }
      }
    </div>
  </div>
</main>
